/**
 * Pure helpers for the career Curriculum tab, the admin track checklist and outside evidence in
 * the portfolio. Nothing here fetches or renders: the server assembles the curriculum and decides
 * every state, and these helpers only label, link and order what it returned.
 */
import { EXTERNAL_SUMMARY_MAX, EXTERNAL_TITLE_MAX, masteryLabel, validateExternalEvidence, type EvidenceOrigin, type ExternalEvidence, type ExternalEvidenceOrigin, type MasteryState } from '@worksim/engine';
import type { IconName } from '../components/ui/Icon';
import { href } from '../router';
import { mapTone, usageHref } from './skills';
import type { ChecklistItem, ChecklistStatus, ChecklistSummary, CompactCurriculumSkill, CurriculumItemRef } from '../api/curriculum';

const enc = encodeURIComponent;

/* ------------------------------------------------------------------ curriculum items */

/** How each kind of item reads on the ladder. */
export const ITEM_KIND: Record<string, { label: string; icon: IconName }> = {
  career: { label: 'Career', icon: 'briefcase' },
  skill: { label: 'Skill', icon: 'graph' },
  'skill-guide': { label: 'Guide', icon: 'list' },
  lesson: { label: 'Lesson', icon: 'book' },
  'knowledge-topic': { label: 'Knowledge topic', icon: 'lightbulb' },
  'practice-bank': { label: 'Practice questions', icon: 'clipboard' },
  lab: { label: 'Lab', icon: 'terminal' },
  'code-lab': { label: 'Code lab', icon: 'code' },
  'vm-lab': { label: 'VM lab', icon: 'server' },
  scenario: { label: 'Scenario', icon: 'briefcase' },
  project: { label: 'Project', icon: 'folder' },
  assessment: { label: 'Assessment', icon: 'check' },
  'interview-question': { label: 'Interview question', icon: 'target' },
  resource: { label: 'Resource', icon: 'map' },
  'cheat-sheet': { label: 'Cheat sheet', icon: 'clipboard' },
  flashcard: { label: 'Flashcard', icon: 'lightbulb' },
  review: { label: 'Review', icon: 'clock' },
};

export const itemKind = (kind: string): { label: string; icon: IconName } => ITEM_KIND[kind] ?? { label: kind.replace(/-/g, ' '), icon: 'arrow-right' };

/**
 * Where an item on the ladder opens. A skill opens its guide when it has one (the guide is the
 * skill's teaching home), otherwise its skill page; interview questions open the career's kit.
 */
export function curriculumItemHref(item: Pick<CurriculumItemRef, 'kind' | 'id' | 'skillIds'>, careerId: string, hasGuide: (skillId: string) => boolean = () => false): string {
  switch (item.kind) {
    case 'skill':
      return hasGuide(item.id) ? guideHref(item.id) : href({ page: 'skill', id: item.id });
    case 'skill-guide':
      return guideHref(item.id);
    case 'interview-question':
      return href({ page: 'interview-kit', careerId });
    case 'career':
      return href({ page: 'career', id: item.id });
    case 'flashcard':
    case 'review':
      return item.skillIds[0] ? guideHref(String(item.skillIds[0])) : href({ page: 'guides' });
    default:
      return usageHref(item.kind, item.id, careerId, item.skillIds[0] ? String(item.skillIds[0]) : undefined);
  }
}

export const guideHref = (skillId: string): string => href({ page: 'guide', id: skillId });

/** "Done", "To do", or nothing for a visitor (the server sends no `done` then). */
export function itemStateWords(item: Pick<CurriculumItemRef, 'done'>): string | undefined {
  if (item.done === undefined) return undefined;
  return item.done ? 'Done' : 'To do';
}

/** "3 of 12 done" when signed in, "12 items" otherwise; the career goal counts responsibilities. */
export function stepCountLine(stepId: string, counts: { total: number; done?: number }): string {
  const noun = stepId === 'career-goal' ? ['responsibility', 'responsibilities'] : stepId === 'next-skill' ? ['skill ready to start', 'skills ready to start'] : ['item', 'items'];
  const word = counts.total === 1 ? noun[0] : noun[1];
  if (counts.done === undefined) return `${counts.total} ${word}`;
  return `${counts.done} of ${counts.total} ${counts.total === 1 ? noun[0] : noun[1]} done`;
}

/** 0..1 share done, or undefined for a visitor or an empty step. */
export function stepRatio(counts: { total: number; done?: number }): number | undefined {
  if (counts.done === undefined || counts.total === 0) return undefined;
  return Math.max(0, Math.min(1, counts.done / counts.total));
}

/** Items beyond the ones shown: the server sends at most twelve per step. */
export const moreCount = (counts: { total: number }, shown: number): number => Math.max(0, counts.total - shown);

/* ------------------------------------------------------------------ skill map */

/** Badge tone for a mastery state; the state is always printed in words too. */
export function stateTone(state: MasteryState | undefined): string {
  const tone = mapTone(state);
  return tone === 'mastered' ? 'ok' : tone === 'practicing' ? 'cyan' : tone === 'review' ? 'warn' : '';
}

export const stateWords = (state: MasteryState | undefined): string => masteryLabel(state ?? 'not-started');

/** "Guide: Beginner, level 3 of 10 open", "Guide: 10 levels" for a visitor, or "No guide yet". */
export function guideLine(skill: Pick<CompactCurriculumSkill, 'guide'>): string {
  const g = skill.guide;
  if (!g) return 'No guide yet';
  if (g.label && g.unlocked !== undefined) return `Guide: ${g.label}, ${g.unlocked} of ${g.levels} levels open`;
  return `Guide: ${g.levels} levels`;
}

/** Where a skill on the map opens: its guide, else its skill page. */
export const skillHref = (skill: { skillId: string; guide?: unknown }): string => (skill.guide ? guideHref(skill.skillId) : href({ page: 'skill', id: skill.skillId }));

/** Every skill id of the curriculum that has a guide. */
export function guideSkillIds(map: { skills: { skillId: string; guide?: unknown }[] }[]): Set<string> {
  const out = new Set<string>();
  for (const t of map) for (const s of t.skills) if (s.guide) out.add(s.skillId);
  return out;
}

/** "4 of 16 skills demonstrated" for the skill map header, or the plain count for a visitor. */
export function skillMapLine(map: { skills: { state?: MasteryState }[] }[]): string {
  const all = map.flatMap((t) => t.skills);
  const signedIn = all.some((s) => s.state !== undefined);
  if (!signedIn) return `${all.length} skill${all.length === 1 ? '' : 's'} across ${map.length} tier${map.length === 1 ? '' : 's'}`;
  const shown = all.filter((s) => s.state === 'demonstrated' || s.state === 'strong-evidence').length;
  return `${shown} of ${all.length} skill${all.length === 1 ? '' : 's'} demonstrated`;
}

/* ------------------------------------------------------------------ track checklist */

export const CHECKLIST_STATUS_LABEL: Record<ChecklistStatus, string> = {
  complete: 'Complete',
  partial: 'Partial',
  missing: 'Missing',
  'not-applicable': 'Not applicable',
};

export const CHECKLIST_TONE: Record<ChecklistStatus, string> = { complete: 'ok', partial: 'warn', missing: 'bad', 'not-applicable': '' };

/** Items whose missing skill ids are best fixed in the guide editor. */
const GUIDE_ITEMS = new Set(['skill-guides', 'expert-content', 'mastery-challenges']);

/**
 * The missing ids of one checklist item as links: skill ids open the admin guide editor for
 * guide-related items and the skill page otherwise; other ids (a tier, "capstone") stay text.
 */
export function missingLinks(item: Pick<ChecklistItem, 'id' | 'missing'>, skillName: (id: string) => string | undefined): { id: string; label: string; href?: string }[] {
  return item.missing.map((id) => {
    const name = skillName(id);
    if (!name) return { id, label: id.replace(/-/g, ' ') };
    return { id, label: name, href: GUIDE_ITEMS.has(item.id) ? `#/admin/guides/${enc(id)}` : href({ page: 'skill', id }) };
  });
}

/** Status counts for a checklist header. */
export function checklistCounts(items: Pick<ChecklistItem, 'status'>[]): Record<ChecklistStatus, number> {
  const out: Record<ChecklistStatus, number> = { complete: 0, partial: 0, missing: 0, 'not-applicable': 0 };
  for (const i of items) out[i.status] += 1;
  return out;
}

/** Tracks with the most gaps first, then by name; planned tracks can be hidden. */
export function sortTracks(rows: ChecklistSummary[], opts: { availableOnly?: boolean } = {}): ChecklistSummary[] {
  return rows.filter((r) => !opts.availableOnly || r.status === 'available').sort((a, b) => b.total - b.complete - (a.total - a.complete) || a.careerName.localeCompare(b.careerName));
}

/* ------------------------------------------------------------------ outside evidence */

/** Badge text for every evidence origin. */
export const ORIGIN_LABEL: Record<EvidenceOrigin, string> = {
  simulation: 'Simulation',
  lab: 'Lab',
  academic: 'Academic',
  personal: 'Personal',
  employment: 'Employment',
};

export const ORIGIN_TONE: Record<EvidenceOrigin, string> = { simulation: 'accent', lab: 'cyan', academic: 'violet', personal: 'violet', employment: 'violet' };

/** The origins a learner may choose, with what each means in plain words. */
export const ORIGIN_OPTIONS: { id: ExternalEvidenceOrigin; label: string; description: string }[] = [
  { id: 'academic', label: 'Academic', description: 'Coursework, a class project, a thesis or a paper from school, college or a course.' },
  { id: 'personal', label: 'Personal', description: 'Something you built or ran on your own time: a home lab, an open-source contribution, a side project.' },
  { id: 'employment', label: 'Employment', description: 'Work you did in a real job or internship, described in your own words. WorkSim cannot check it.' },
];

export interface ExternalDraft {
  careerId: string;
  origin: ExternalEvidenceOrigin | '';
  title: string;
  summary: string;
  skillIds: string[];
  link: string;
  completedAt: string;
}

export const emptyExternalDraft = (careerId = ''): ExternalDraft => ({ careerId, origin: '', title: '', summary: '', skillIds: [], link: '', completedAt: '' });

/**
 * The same checks the server runs (without the registry), so the form can say what is wrong
 * before sending. The server checks again and has the final word.
 */
export function externalDraftProblems(draft: ExternalDraft): string[] {
  const problems: string[] = [];
  if (!draft.careerId) problems.push('Choose the track this evidence relates to.');
  if (!draft.origin) problems.push('Choose where the work comes from.');
  if (!draft.completedAt) problems.push('Add the date you finished it.');
  if (!draft.origin || !draft.completedAt) return [...problems, ...validateExternalEvidence(toEvidence(draft)).filter((p) => !/^Origin must|^completedAt/.test(p))];
  return [...problems, ...validateExternalEvidence(toEvidence(draft)).map((p) => (p.startsWith('completedAt') ? 'The completion date is not a date.' : p))];
}

function toEvidence(draft: ExternalDraft): ExternalEvidence {
  const out: ExternalEvidence = { id: 'draft', careerId: draft.careerId as ExternalEvidence['careerId'], origin: (draft.origin || 'personal') as ExternalEvidenceOrigin, title: draft.title, summary: draft.summary, skillIds: draft.skillIds as ExternalEvidence['skillIds'], completedAt: draft.completedAt };
  if (draft.link.trim()) out.link = draft.link.trim();
  return out;
}

/** The request body for a draft that passed `externalDraftProblems`. */
export function externalInput(draft: ExternalDraft): { careerId: string; origin: ExternalEvidenceOrigin; title: string; summary: string; skillIds: string[]; link?: string; completedAt: string } {
  return {
    careerId: draft.careerId,
    origin: draft.origin as ExternalEvidenceOrigin,
    title: draft.title.trim(),
    summary: draft.summary.trim(),
    skillIds: draft.skillIds,
    ...(draft.link.trim() ? { link: draft.link.trim() } : {}),
    completedAt: draft.completedAt,
  };
}

export { EXTERNAL_SUMMARY_MAX, EXTERNAL_TITLE_MAX };

/** "812 characters left" under a text field. */
export const charsLeft = (text: string, max: number): string => {
  const left = max - text.trim().length;
  return left >= 0 ? `${left.toLocaleString('en-US')} character${left === 1 ? '' : 's'} left` : `${(-left).toLocaleString('en-US')} character${left === -1 ? '' : 's'} over`;
};

/** A link supplied by the learner opens only when it is an absolute https URL. */
export const isHttpsLink = (ref: string): boolean => /^https:\/\/\S+$/i.test(ref);
